import { UiLanguage } from '@today/types'
import { z } from 'zod'

/** `users.display_name` is `varchar(128)` (Postgres counts characters, i.e. code points). */
const USER_DISPLAY_NAME_MAX_LENGTH = 128

/**
 * `PATCH /users/me/profile` (TO-197 user-self-service-settings REQ-004).
 *
 * Every rule rejects, never clamps. Strict at both levels, so `userId`,
 * `tenantId` or any admin-owned field in the body is a 422 rather than being
 * silently dropped — the caller is always the DB-resolved principal.
 */

/** Control (`Cc`) and format (`Cf`, e.g. U+200B, the bidi overrides) characters. */
const CONTROL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u

/** Trimmed, 1–128 code points, no control/format characters. Homoglyphs are NOT detected (stated limit). */
const displayName = z
	.string()
	.trim()
	.refine((value) => {
		const length = [...value].length
		return length >= 1 && length <= USER_DISPLAY_NAME_MAX_LENGTH
	}, `must be 1–${USER_DISPLAY_NAME_MAX_LENGTH} characters after trimming`)
	.refine((value) => !CONTROL_OR_FORMAT.test(value), 'must not contain control or format characters')

/** The IANA zones the runtime knows, plus `UTC` (absent from some ICU lists). */
const TIME_ZONES: ReadonlySet<string> = new Set([...Intl.supportedValuesOf('timeZone'), 'UTC'])

/** An IANA timezone identifier, matched case-sensitively; at most 64 characters. */
export const timezoneSchema = z
	.string()
	.max(64)
	.refine((value) => TIME_ZONES.has(value), 'must be an IANA timezone identifier')

/** E.164: `+`, then 2–15 digits, the first not `0`. */
export const e164PhoneSchema = z.string().regex(/^\+[1-9][0-9]{1,14}$/, 'must be an E.164 phone number')

const personalContact = z
	.strictObject({
		phone: e164PhoneSchema.nullable().optional(),
		email: z.email().max(254).nullable().optional(),
	})
	.refine((value) => Object.keys(value).length > 0, 'must set at least one of phone, email')

export const userProfilePatchSchema = z
	.strictObject({
		displayName: displayName.nullable().optional(),
		language: z.enum(UiLanguage).nullable().optional(),
		timezone: timezoneSchema.nullable().optional(),
		showPersonalContactOnOffers: z.boolean().optional(),
		personalContact: personalContact.optional(),
	})
	.refine((value) => Object.keys(value).length > 0, 'must set at least one field')

/** `PATCH /users/me/profile` body (TO-197). */
export type UserProfilePatchInput = z.infer<typeof userProfilePatchSchema>

/** The stored `users.personal_contact_overrides` JSON, re-parsed on read: unknown keys are dropped. */
export const storedPersonalContactSchema = z.object({
	phone: e164PhoneSchema.optional().catch(undefined),
	email: z.email().max(254).optional().catch(undefined),
})
